import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Popconfirm } from 'antd';
import {
  useAcceptTaskRecommendationMutation,
  useDeclineTaskRecommendationMutation,
} from 'app/api';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { selectIsConsultant } from 'app/containers/Authorization/slice/selectors';
import ConfigProviderWrapper from 'app/containers/ConfigProviderWrapper/ConfigProviderWrapper';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import {
  detailsViewTypeEnum,
  TaskStatusType,
} from 'app/containers/Task/slice/types';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import { RouteConstants } from 'app/routes';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

interface IProps {
  taskId: number;
  status?: TaskStatusType;
}

const TaskDetailsHeaderButtons: FC<IProps> = ({ taskId, status }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [form] = Form.useForm();

  const [
    acceptRecommendation,
    { isLoading: isAcceptRecommendationLoading },
  ] = useAcceptTaskRecommendationMutation();

  const [
    declineRecommendation,
    { isLoading: isDeclineRecommendationLoading },
  ] = useDeclineTaskRecommendationMutation();

  const envId = useSelector(selectSelectedEnvId) as number;
  const isConsultant = useSelector(selectIsConsultant);

  const makeAllTaskFieldsDone = () => {
    history.push(
      generatePath(RouteConstants.task.taskProgressUpdate, {
        envId,
        taskId,
      }).concat(`/?all-fields-done=true&tab=${detailsViewTypeEnum.resources}`),
    );
  };

  const onRecommendationAccept = async () => {
    try {
      await acceptRecommendation({ envId, taskId }).unwrap();

      history.push(
        generatePath(RouteConstants.task.list, {
          envId,
        }),
      );
    } catch (error) {
      handleError(error);
    }
  };

  const onRecommendationDeclineConfirm = async ({
    comment,
  }: {
    comment: string;
  }) => {
    try {
      await declineRecommendation({ envId, taskId, comment }).unwrap();

      history.push(
        generatePath(RouteConstants.task.list, {
          envId,
        }),
      );
    } catch (error) {
      handleError(error);
    }
  };

  const onRecommendationDecline = () => {
    Modal.confirm({
      title: t(
        'Task.Details.RecommendationCancelReason',
        'Reason for rejection of the proposal?',
      ),

      content: (
        <ConfigProviderWrapper>
          <Form
            form={form}
            id="recommendationDeclineForm"
            onFinish={onRecommendationDeclineConfirm}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item name="comment" rules={[{ required: true }]}>
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </ConfigProviderWrapper>
      ),
      okButtonProps: {
        form: 'recommendationDeclineForm',
        htmlType: 'submit',
      },

      okText: t('Common.Confirm', 'Confirm'),
      cancelText: t('Common.Cancel', 'Cancel'),
      onOk() {
        return new Promise<void>((resolve, reject) => {
          form
            .validateFields()
            .then(() => resolve())
            .catch(() => reject());
        });
      },
    });
  };

  const showConfirmAllDoneModal = (
    e?: React.MouseEvent<HTMLElement, MouseEvent> | undefined,
  ) => {
    Modal.confirm({
      title: t(
        'Task.Details.DidYouReallyAllDoneTitle',
        'Did you really complete the task in all the fields?',
      ),
      icon: <ExclamationCircleOutlined />,
      content: t(
        'Task.Details.DidYouReallyAllDoneText',
        'If you have completed the task in all fields, confirm. It will not be possible to reset this selection.',
      ),
      okText: t('Task.Details.Confirm', 'Confirm'),
      cancelText: t('Task.Details.Cancel', 'Cancel'),
      onOk() {
        makeAllTaskFieldsDone();
      },
    });
  };

  const redirectToEditTaskPage = () =>
    history.push(
      generatePath(RouteConstants.task.editTask, {
        envId,
        taskId,
      }),
    );

  const redirectToTaskProgressPage = () =>
    history.push(
      generatePath(RouteConstants.task.taskProgressUpdate, {
        envId,
        taskId,
      }),
    );

  const headerButtons = [];

  if (status === 'RECOMMENDED') {
    if (isConsultant) {
      headerButtons.push(
        <Button key="0" onClick={redirectToEditTaskPage}>
          {t('Common.Edit', 'Edit')}
        </Button>,
      );
    } else {
      headerButtons.push(
        <Button
          key="11"
          onClick={onRecommendationDecline}
          loading={isDeclineRecommendationLoading}
        >
          {t('Common.Discard', 'Discard')}
        </Button>,
        <Popconfirm
          key="12"
          placement="bottomRight"
          title={t(
            'Task.Details.AreYouSureApproveRecommendation',
            "Once the 'Recommendation' is approved, the task will become 'Planned'. Do you want to continue?",
          )}
          onConfirm={onRecommendationAccept}
          okText={t('Common.Yes', 'Yes')}
          cancelText={t('Common.No', 'No')}
        >
          <Button
            type="primary"
            color="#52C41A"
            style={{ backgroundColor: '#52C41A', border: '1px solid #52C41A' }}
            loading={isAcceptRecommendationLoading}
          >
            {t('Common.Approve', 'Approve')}
          </Button>
        </Popconfirm>,
      );
    }
  } else if (status === 'PLANNED' || status === 'IN_PROGRESS') {
    headerButtons.push(
      <Button key="0" onClick={redirectToEditTaskPage}>
        {t('Task.Details.EditTheTask', 'Edit the task')}
      </Button>,
      <Popconfirm
        key="1"
        placement="bottomRight"
        title={t(
          'Task.Details.DidYouCompleteTheTask',
          'Did you complete the task in all planned fields?',
        )}
        onConfirm={showConfirmAllDoneModal}
        onCancel={redirectToTaskProgressPage}
        okText={t('Task.Details.AllFields', 'All fields')}
        cancelText={t('Task.Details.PartOfTheFields', 'Part of the fields')}
      >
        <Button type="primary">
          {t('Task.Details.CompleteTheTask', 'Complete the task')}
        </Button>
      </Popconfirm>,
    );
  } else if (status === 'COMPLETED') {
    headerButtons.push(
      <Button type="primary" onClick={redirectToTaskProgressPage}>
        {t('Task.Details.ChangeTaskProgress', 'Change task progress')}
      </Button>,
    );
  }
  return (
    <PermissionsWrapper
      requiredEditPermissions={[PermissionsEnum.taskAdministration]}
    >
      {headerButtons}
    </PermissionsWrapper>
  );
};

export default TaskDetailsHeaderButtons;
